import { $, $$, pageData, setLoading } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { findSelectMenu } from '../lib/select-menu.js';
import { toast, confirmAction } from '../lib/toast.js';

/**
 * The five social façades, driven by one controller.
 *
 * The form both creates and edits: clicking *Edit* on a row fills it and
 * switches the submit button's label. A page per subscription would have been
 * five more views for a ten-field form.
 */

interface Watch {
  _id: string;
  platform: string;
  sourceId: string;
  sourceLabel: string;
  channelId: string;
  speed: string;
  enabled: boolean;
  mentionRoleIds: string[];
  mentionEveryone: boolean;
  message: string;
  format: string;
  templateName?: string;
  keywords: string[];
  minScore: number;
  minAgeSeconds: number;
  nsfwMode: string;
  contentTypes: string[];
  events?: { kind: string; enabled: boolean; message: string }[];
  autoPublish: boolean;
  minViewers: number;
  categoryFilter: string;
}

interface PageData {
  guildId: string;
  platform: string;
  watches: Watch[];
}

/**
 * Disconnecting the TikTok account.
 *
 * An act, not a setting: it takes effect on the click, so it escapes the save
 * bar — the same choice the Reports page makes for unblocking. Putting it
 * behind "Save" would suggest it could be taken back.
 */
function wireTikTokUnlink(guildId: string): void {
  // Every button, each carrying the account it disconnects: with several
  // linked, one nameless button would silence whichever came first.
  for (const button of $$<HTMLButtonElement>('[data-tiktok-unlink]')) {
    const openId = button.dataset.tiktokUnlink ?? '';

    button.addEventListener('click', () => {
      void confirmAction(ct('confirmUnlinkTikTok', 'Disconnect this TikTok account?')).then(
        (confirmed) => {
          if (!confirmed) return;

          void api.delete(`/api/social/tiktok/link/${openId}?guildId=${guildId}`).then(() => {
            location.reload();
          });
        },
      );
    });
  }
}

/**
 * Reads the per-event blocks as they stand on the page.
 *
 * An event with no wording of its own sends an empty string, which the service
 * reads as "use the general message" — the box is for the exception, not a
 * field everybody has to fill three times.
 */
function readEvents(form: HTMLFormElement): { kind: string; enabled: boolean; message: string }[] {
  return $$<HTMLElement>('[data-event]', form).map((row) => ({
    kind: row.dataset.event ?? '',
    enabled: $<HTMLInputElement>('[data-event-enabled]', row)?.checked ?? true,
    message: $<HTMLTextAreaElement>('[data-event-message]', row)?.value.trim() ?? '',
  }));
}

/**
 * The menu of sources already used.
 *
 * Picking one fills the field, so the *Check* step still runs and the stored
 * identifier still comes from the provider — the menu is a shortcut for typing,
 * never a way around resolution.
 *
 * The forget button appears only once something is selected, and it removes the
 * entry from the menu alone: no watch is touched, because tidying a dropdown
 * must never take a live alert off the air.
 */
function wireSavedAccounts(form: HTMLFormElement, guildId: string): void {
  const picker = $<HTMLSelectElement>('[data-account-pick]', form);
  const forget = $<HTMLButtonElement>('[data-account-forget]', form);
  const source = form.elements.namedItem('source');
  if (!picker || !forget || !(source instanceof HTMLInputElement)) return;

  const selected = () => picker.options[picker.selectedIndex];

  picker.addEventListener('change', () => {
    const option = selected();
    if (!option?.value) {
      forget.classList.add('hidden');
      return;
    }

    source.value = option.value;
    source.dispatchEvent(new Event('input', { bubbles: true }));
    forget.classList.toggle('hidden', option.dataset.oauth === 'true');
  });

  forget.addEventListener('click', () => {
    const option = selected();
    const id = option?.dataset.id;
    if (!id) return;

    void confirmAction(ct('confirmForgetAccount', 'Remove it from the menu?')).then((ok) => {
      if (!ok) return;

      void api.delete(`/api/social/accounts/${id}?guildId=${guildId}`).then(() => {
        option.remove();
        picker.value = '';
        forget.classList.add('hidden');
        toast(ct('accountForgotten', 'Removed from the menu. Your alerts are untouched.'));
      });
    });
  });
}

/**
 * The test send.
 *
 * Reads the form as it stands rather than what was saved: somebody adjusting
 * their wording wants to see *that* wording, not the one from last week.
 */
function wireTestSend(form: HTMLFormElement, guildId: string, platform: string): void {
  const button = $<HTMLButtonElement>('[data-test-send]', form);
  if (!button) return;

  button.addEventListener('click', () => {
    const channelId = findSelectMenu(form, 'channelId')?.single ?? '';
    if (!channelId) {
      toast(ct('testSendNoChannel', 'Choose a channel first.'), 'error');
      return;
    }

    setLoading(button, true);

    void api
      .post<{ ok: boolean; reason: string }>(`/api/social/test-send?guildId=${guildId}`, {
        guildId,
        platform,
        channelId,
        /*
         * The source as it stands on the page: testing anything other than what
         * is on screen would answer a question nobody asked.
         *
         * Read both ways because TikTok's source is a menu — an upgraded
         * `<select>` keeps its value in a hidden input, which `text` finds, but
         * asking the menu directly is the shorter road when there is one.
         */
        source: findSelectMenu(form, 'source')?.single ?? text(form, 'source'),
        mentionRoleIds: findSelectMenu(form, 'mentionRoleIds')?.value ?? [],
        mentionEveryone: checked(form, 'mentionEveryone'),
        message: text(form, 'message'),
        format: text(form, 'format') || 'embed',
      })
      .then((result) => {
        toast(
          ct(result.ok ? 'testSendSent' : `testSend_${result.reason}`, 'Done.'),
          result.ok ? 'success' : 'error',
        );
      })
      .finally(() => {
        setLoading(button, false);
      });
  });
}

export function initSocialPage(): void {
  const form = $<HTMLFormElement>('[data-social-form]');
  if (!form) return;

  const { guildId, platform, watches } = pageData<PageData>();

  wireTikTokUnlink(guildId);
  wireTestSend(form, guildId, platform);
  wireSavedAccounts(form, guildId);

  /** The subscription being edited, or `''` while creating. */
  let editing = '';
  /** Filled by *Check*, so the stored id is canonical and not what was typed. */
  let resolved: { id: string; label: string } | null = null;

  const resolveButton = $<HTMLButtonElement>('[data-resolve]', form);
  const resolvedNote = $<HTMLElement>('[data-resolved]', form);

  resolveButton?.addEventListener('click', () => {
    void run(resolveButton, async () => {
      const source = text(form, 'source');
      if (!source) return;

      const body = await api.post<{
        ok: boolean;
        source?: { id: string; label: string };
        reasonKey?: string;
      }>(`/api/social/resolve?guildId=${guildId}`, { guildId, platform, source });

      if (!body.ok || !body.source) {
        resolved = null;
        if (resolvedNote)
          resolvedNote.textContent = ct(body.reasonKey ?? 'socialNotFound', 'Not found.');
        return;
      }

      resolved = body.source;
      if (resolvedNote) resolvedNote.textContent = body.source.label;
    });
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (!submit) return;

    void run(submit, async () => {
      const source = text(form, 'source');
      const channelId = findSelectMenu(form, 'channelId')?.single ?? '';

      if (!source || !channelId) {
        toast(ct('socialMissingFields', 'Pick a source and a channel.'), 'error');
        return;
      }

      /*
       * The canonical identifier wins over what was typed.
       *
       * Without "Check", the raw input is sent back and the server resolves it
       * itself: the same path, one request cheaper for somebody who already
       * knows what they are pasting.
       */
      const payload = {
        guildId,
        platform,
        sourceId: resolved?.id ?? source,
        sourceLabel: resolved?.label ?? '',
        channelId,
        speed: value(form, 'speed') || 'slow',
        enabled: true,
        templateName: value(form, 'templateName'),
        mentionRoleIds: findSelectMenu(form, 'mentionRoleIds')?.value ?? [],
        mentionEveryone: checked(form, 'mentionEveryone'),
        message: text(form, 'message'),
        format: text(form, 'format') || 'embed',
        keywords: splitKeywords(text(form, 'keywords')),
        minScore: number(form, 'minScore'),
        minAgeSeconds: number(form, 'minAgeSeconds'),
        nsfwMode: value(form, 'nsfwMode') || 'none',
        contentTypes: multi(form, 'contentTypes'),
        events: readEvents(form),
        autoPublish: checked(form, 'autoPublish'),
        onStreamEnd: 'nothing',
        autoRefresh: false,
        minViewers: number(form, 'minViewers'),
        categoryFilter: text(form, 'categoryFilter'),
      };

      if (editing) await api.put(`/api/social/${editing}?guildId=${guildId}`, payload);
      else await api.post(`/api/social?guildId=${guildId}`, payload);

      toast(ct('socialSaved', 'Alert saved.'), 'success');
      location.reload();
    });
  });

  $('[data-cancel-edit]')?.addEventListener('click', () => {
    editing = '';
    resolved = null;
    form.reset();
    refreshMode(form, editing);
  });

  $('#social-list')?.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const edit = target.closest<HTMLButtonElement>('[data-edit]');
    if (edit) {
      const watch = watches.find((row) => row._id === edit.getAttribute('data-edit'));
      if (watch) {
        editing = watch._id;
        fill(form, watch);
        refreshMode(form, editing);
        form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    const remove = target.closest<HTMLButtonElement>('[data-remove]');
    if (!remove) return;

    const id = remove.getAttribute('data-remove') ?? '';
    if (!id || !confirm(ct('confirmDeleteSocial', 'Delete this alert?'))) return;

    void run(remove, async () => {
      await api.delete(`/api/social/${id}?guildId=${guildId}`);
      toast(ct('socialDeleted', 'Alert deleted.'), 'success');
      location.reload();
    });
  });
}

/* ------------------------------------------------------------------- champs */

function refreshMode(form: HTMLFormElement, editing: string): void {
  const label = $<HTMLElement>('[data-submit-label]', form);
  if (label)
    label.textContent = ct(editing ? 'socialUpdate' : 'socialAdd', editing ? 'Update' : 'Add');

  const cancel = $<HTMLElement>('[data-cancel-edit]');
  if (cancel) cancel.classList.toggle('hidden', !editing);
}

function fill(form: HTMLFormElement, watch: Watch): void {
  setValue(form, 'source', watch.sourceLabel || watch.sourceId);
  setValue(form, 'keywords', watch.keywords.join(', '));
  setValue(form, 'minScore', String(watch.minScore));
  setValue(form, 'minAgeSeconds', String(watch.minAgeSeconds));
  setValue(form, 'minViewers', String(watch.minViewers));
  setValue(form, 'categoryFilter', watch.categoryFilter);

  const speed = form.elements.namedItem('speed');
  if (speed instanceof HTMLSelectElement) speed.value = watch.speed;

  /*
   * The wording, the shape and the template.
   *
   * Left out until 6 Oct 2026: editing showed an empty message box, and saving
   * without retyping it erased the server's wording.
   */
  const message = form.elements.namedItem('message');
  if (message instanceof HTMLTextAreaElement) message.value = watch.message;

  const template = form.elements.namedItem('templateName');
  if (template instanceof HTMLSelectElement) template.value = watch.templateName ?? '';

  // Setting the menu does not fire its `onChange`, so the hidden input the
  // save reads is written as well.
  const format = findSelectMenu(form, 'format');
  if (format) format.value = [watch.format || 'embed'];
  setValue(form, 'format', watch.format || 'embed');

  const nsfw = form.elements.namedItem('nsfwMode');
  if (nsfw instanceof HTMLSelectElement) nsfw.value = watch.nsfwMode;

  const types = form.elements.namedItem('contentTypes');
  if (types instanceof HTMLSelectElement) {
    for (const option of types.options) option.selected = watch.contentTypes.includes(option.value);
  }

  /*
   * The per-event blocks, restored from what was saved.
   *
   * A watch with no entry keeps the blocks as the page drew them — enabled,
   * empty — which is what an unmigrated watch means: everything is announced.
   */
  for (const row of $$<HTMLElement>('[data-event]', form)) {
    const stored = watch.events?.find((event) => event.kind === row.dataset.event);

    const toggle = $<HTMLInputElement>('[data-event-enabled]', row);
    if (toggle) toggle.checked = stored ? stored.enabled : true;

    const box = $<HTMLTextAreaElement>('[data-event-message]', row);
    if (box) box.value = stored?.message ?? '';
  }

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = watch.channelId ? [watch.channelId] : [];

  const roles = findSelectMenu(form, 'mentionRoleIds');
  if (roles) roles.value = watch.mentionRoleIds;

  for (const flag of ['mentionEveryone', 'autoPublish'] as const) {
    const node = form.elements.namedItem(flag);
    if (node instanceof HTMLInputElement) node.checked = watch[flag];
  }
}

/** Comma or newline separated, empties dropped. */
function splitKeywords(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((word) => word.trim())
    .filter(Boolean)
    .slice(0, 20);
}

async function run(button: HTMLButtonElement, action: () => Promise<void>): Promise<void> {
  setLoading(button, true);
  try {
    await action();
  } catch (error) {
    toast(error instanceof Error ? error.message : String(error), 'error');
  } finally {
    setLoading(button, false);
  }
}

function text(form: HTMLFormElement, name: string): string {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function value(form: HTMLFormElement, name: string): string {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLSelectElement ? node.value : '';
}

function multi(form: HTMLFormElement, name: string): string[] {
  const node = form.elements.namedItem(name);
  if (!(node instanceof HTMLSelectElement)) return [];
  return $$<HTMLOptionElement>('option', node)
    .filter((option) => option.selected)
    .map((option) => option.value);
}

function setValue(form: HTMLFormElement, name: string, next: string): void {
  const node = form.elements.namedItem(name);
  if (node instanceof HTMLInputElement) node.value = next;
}

function number(form: HTMLFormElement, name: string): number {
  const node = form.elements.namedItem(name);
  const parsed = node instanceof HTMLInputElement ? Number(node.value) : 0;
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function checked(form: HTMLFormElement, name: string): boolean {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement ? node.checked : false;
}
